<template>
    <div class="glass-container">
        <div class="finance-companies-container">
            <!-- Main Card -->
            <div class="modern-card">
                <!-- Header Section -->
                <div class="header-section">
                    <div class="title-section">
                        <h1 class="page-title">Finance Companies</h1>
                        <p class="page-subtitle">Manage finance company information and tax settings</p>
                    </div>

                    <div class="action-buttons">
                        <b-button @click="add_new" variant="primary" class="action-btn add-btn">
                            <b-icon icon="plus-circle" class="mr-2"></b-icon>
                            Add Company
                        </b-button>
                        <b-button @click="fetch_init" variant="outline-secondary" class="action-btn refresh-btn">
                            <b-icon icon="arrow-clockwise" class="mr-2"></b-icon>
                            Refresh
                        </b-button>
                    </div>
                </div>

                <!-- Info Alert -->
                <b-alert variant="info" show class="info-alert">
                    <div class="alert-content">
                        <b-icon icon="info-circle" class="mr-2"></b-icon>
                        <div>
                            <strong>Important:</strong> This tool contains list of finance companies in the system.
                            You can add new finance companies here. If you change a name, phone number, or state tax
                            exemption for any existing finance company, please click <strong>SAVE</strong> button
                            once done changing the entry.
                        </div>
                    </div>
                </b-alert>

                <!-- Loading State -->
                <div v-if="items_being_loaded" class="loading-container">
                    <loading
                        :active="items_being_loaded"
                        :can-cancel="false"
                        :is-full-page="false"
                    ></loading>
                </div>

                <!-- Finance Companies List -->
                <div v-else class="companies-list-container">
                    <div v-if="items.length > 0">
                        <div v-for="(item, idx) in items" :key="idx" class="company-card">
                            <!-- Company Basic Info -->
                            <div class="company-header">
                                <div class="company-status">
                                    <span class="field-label">STATUS</span>
                                    <div class="status-toggle">
                                        <b-form-checkbox
                                            v-model="item.is_active"
                                            switch
                                            size="lg"
                                            class="status-switch"
                                            @change="toggleCompanyStatus(item)"
                                        >
                                            <span :class="{'text-success': item.is_active, 'text-danger': !item.is_active}" class="status-text">
                                                {{ item.is_active ? 'ACTIVE' : 'INACTIVE' }}
                                            </span>
                                        </b-form-checkbox>
                                    </div>
                                </div>
                                <div class="company-name">
                                    <span class="field-label">COMPANY NAME</span>
                                    <b-input
                                        v-model="item.finance_company_name"
                                        size="sm"
                                        class="name-input"
                                        placeholder="Enter company name"
                                        :class="{ 'new-company': !item.finance_company_id }"
                                    ></b-input>
                                </div>

                                <div class="company-phone">
                                    <span class="field-label">PHONE NUMBER</span>
                                    <b-input
                                        v-model="item.finance_company_phone_number"
                                        size="sm"
                                        class="phone-input"
                                        placeholder="Phone number"
                                    ></b-input>
                                </div>
                            </div>

                            <!-- Banner Image Upload -->
                            <div class="banner-section">
                                <span class="field-label">BANNER IMAGE</span>
                                <div class="banner-upload-row">
                                    <!-- Preview -->
                                    <div class="banner-preview">
                                        <img
                                            v-if="item.banner_image"
                                            :src="item.banner_image"
                                            alt="Banner preview"
                                            @error="handleBannerError(item)"
                                        />
                                        <div v-else class="banner-placeholder">
                                            <b-icon icon="image" font-scale="2"></b-icon>
                                            <span>No banner uploaded</span>
                                        </div>
                                        <div v-if="item._banner_uploading" class="banner-overlay">
                                            <b-spinner small variant="light"></b-spinner>
                                        </div>
                                    </div>

                                    <!-- Controls -->
                                    <div class="banner-controls">
                                        <input
                                            :id="'banner-file-' + idx"
                                            type="file"
                                            accept="image/*"
                                            class="banner-file-input"
                                            @change="handleBannerFileChange($event, item)"
                                        />
                                        <label
                                            :for="'banner-file-' + idx"
                                            class="banner-upload-btn"
                                        >
                                            <b-icon icon="cloud-upload" class="mr-1"></b-icon>
                                            {{ item.banner_image ? 'Replace Banner' : 'Upload Banner' }}
                                        </label>

                                        <b-button
                                            v-if="item.banner_image"
                                            variant="outline-danger"
                                            size="sm"
                                            class="banner-remove-btn"
                                            @click="removeBanner(item)"
                                        >
                                            <b-icon icon="trash" class="mr-1"></b-icon>
                                            Remove
                                        </b-button>

                                        <small class="banner-hint">Recommended: 1200×300px (JPG/PNG, max 5MB)</small>
                                    </div>
                                </div>
                            </div>

                            <!-- Company Contact Details -->
                            <div class="company-details-row">
                                <div class="detail-field">
                                    <span class="field-label">WEBSITE</span>
                                    <b-input
                                        v-model="item.finance_company_website"
                                        size="sm"
                                        class="website-input"
                                        placeholder="https://example.com"
                                    ></b-input>
                                </div>

                                <div class="detail-field address-field">
                                    <span class="field-label">ADDRESS</span>
                                    <b-input
                                        v-model="item.finance_company_address"
                                        size="sm"
                                        class="address-input"
                                        placeholder="Company address"
                                    ></b-input>
                                </div>
                            </div>

                            <!-- Tax Collection Section -->
                            <div class="tax-section">
                                <div class="tax-header">
                                    <b-icon icon="building" class="mr-2"></b-icon>
                                    <h6 class="tax-title">Sales Tax Collection by State</h6>
                                </div>
                                <p class="tax-description">
                                    Finance company will collect sales tax in checked states:
                                </p>

                                <div class="states-container">
                                    <div class="states-header">
                                        <span class="states-count">
                                            Selected: {{ item.finance_company_charges_tax_in_states ? item.finance_company_charges_tax_in_states.length : 0 }} states
                                        </span>
                                        <b-button
                                            @click="toggleAllStates(item)"
                                            variant="outline-secondary"
                                            size="sm"
                                            class="toggle-all-btn"
                                        >
                                            {{ isAllStatesSelected(item) ? 'Clear All' : 'Select All' }}
                                        </b-button>
                                    </div>

                                    <div class="states-grid">
                                        <div
                                            v-for="state in states"
                                            :key="state.value"
                                            class="state-checkbox-item"
                                            :class="{ 'selected': isStateSelected(item, state.value) }"
                                            @click="toggleState(item, state.value)"
                                        >
                                            <b-form-checkbox
                                                :checked="isStateSelected(item, state.value)"
                                                class="state-checkbox"
                                            >
                                                <span class="state-label">{{ state.text }}</span>
                                            </b-form-checkbox>
                                        </div>
                                    </div>

                                    <div class="selected-states-preview" v-if="item.finance_company_charges_tax_in_states && item.finance_company_charges_tax_in_states.length > 0">
                                        <span class="preview-label">Selected:</span>
                                        <span class="selected-states">
                                            {{ getSelectedStatesPreview(item) }}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <!-- Action Buttons -->
                            <div class="company-actions">
                                <div class="company-info" v-if="item.finance_company_id">
                                    <span class="company-id">ID: {{ item.finance_company_id }}</span>
                                </div>

                                <div class="action-buttons-wrapper">
                                    <b-button
                                        @click="save_finance_company(item)"
                                        variant="success"
                                        size="sm"
                                        class="action-btn save-btn"
                                        :disabled="!item.finance_company_name"
                                    >
                                        <b-icon icon="check" class="mr-1"></b-icon>
                                        Save
                                    </b-button>

                                    <b-button
                                        @click="remove_finance_company(idx)"
                                        variant="outline-danger"
                                        size="sm"
                                        class="action-btn cancel-btn"
                                        v-if="!item.finance_company_id"
                                    >
                                        <b-icon icon="x" class="mr-1"></b-icon>
                                        Cancel
                                    </b-button>
                                </div>
                            </div>
                        </div>

                        <!-- Table Footer -->
                        <div class="table-footer">
                            <div class="pagination-info">
                                Showing {{ items.length }} finance companies
                            </div>
                        </div>
                    </div>

                    <!-- Empty State -->
                    <div v-else class="empty-state">
                        <div class="empty-state-icon">
                            <b-icon icon="building" font-scale="3"></b-icon>
                        </div>
                        <h3>No Finance Companies Found</h3>
                        <p>No finance companies in the system yet</p>
                        <div class="empty-state-actions mt-3">
                            <b-button @click="add_new" variant="primary">
                                <b-icon icon="plus-circle" class="mr-2"></b-icon>
                                Add First Company
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Requestor from '@/utilities/Requestor'
    import Loading from 'vue-loading-overlay';
    import 'vue-loading-overlay/dist/vue-loading.css';

    export default {
        name: "ListFinanceCompanies",
        components: { Loading },
        data: function() {
            return {
                packet: {},
                items: [],
                items_being_loaded: false,
                states: [
                    {value: 'PA', text: 'PA'},
                    {value: 'NJ', text: 'NJ'},
                    {value: 'DE', text: 'DE'},
                    {value: 'AL', text: 'AL'},
                    {value: 'AK', text: 'AK'},
                    {value: 'AS', text: 'AS'},
                    {value: 'AZ', text: 'AZ'},
                    {value: 'AR', text: 'AR'},
                    {value: 'CA', text: 'CA'},
                    {value: 'CO', text: 'CO'},
                    {value: 'CT', text: 'CT'},
                    {value: 'DC', text: 'DC'},
                    {value: 'FM', text: 'FM'},
                    {value: 'FL', text: 'FL'},
                    {value: 'GA', text: 'GA'},
                    {value: 'GU', text: 'GU'},
                    {value: 'HI', text: 'HI'},
                    {value: 'ID', text: 'ID'},
                    {value: 'IL', text: 'IL'},
                    {value: 'IN', text: 'IN'},
                    {value: 'IA', text: 'IA'},
                    {value: 'KS', text: 'KS'},
                    {value: 'KY', text: 'KY'},
                    {value: 'LA', text: 'LA'},
                    {value: 'ME', text: 'ME'},
                    {value: 'MH', text: 'MH'},
                    {value: 'MD', text: 'MD'},
                    {value: 'MA', text: 'MA'},
                    {value: 'MI', text: 'MI'},
                    {value: 'MN', text: 'MN'},
                    {value: 'MS', text: 'MS'},
                    {value: 'MO', text: 'MO'},
                    {value: 'MT', text: 'MT'},
                    {value: 'NE', text: 'NE'},
                    {value: 'NV', text: 'NV'},
                    {value: 'NH', text: 'NH'},
                    {value: 'NM', text: 'NM'},
                    {value: 'NY', text: 'NY'},
                    {value: 'NC', text: 'NC'},
                    {value: 'ND', text: 'ND'},
                    {value: 'MP', text: 'MP'},
                    {value: 'OH', text: 'OH'},
                    {value: 'OK', text: 'OK'},
                    {value: 'OR', text: 'OR'},
                    {value: 'PW', text: 'PW'},
                    {value: 'PR', text: 'PR'},
                    {value: 'RI', text: 'RI'},
                    {value: 'SC', text: 'SC'},
                    {value: 'SD', text: 'SD'},
                    {value: 'TN', text: 'TN'},
                    {value: 'TX', text: 'TX'},
                    {value: 'UT', text: 'UT'},
                    {value: 'VT', text: 'VT'},
                    {value: 'VI', text: 'VI'},
                    {value: 'VA', text: 'VA'},
                    {value: 'WA', text: 'WA'},
                    {value: 'WV', text: 'WV'},
                    {value: 'WI', text: 'WI'},
                    {value: 'WY', text: 'WY'},
                ],
            }
        },
        methods: {
            toggleCompanyStatus: function(item) {
                // Save the company with the updated status
                this.save_finance_company(item);

                let statusText = item.is_active ? 'activated' : 'deactivated';
                this.$bvToast.toast(
                    `Company ${item.finance_company_name} has been ${statusText}.`,
                    {
                        title: "Status Updated",
                        autoHideDelay: 1500,
                        variant: item.is_active ? "success" : "warning"
                    }
                );
            },

            remove_finance_company: function(idx) {
                this.items.splice(idx, 1);
            },

            save_finance_company: function(item) {
                let path = "/api/finance_company/save_finance_company/";

                // If banner_image key is missing on the item, ensure we send "" only
                // when the user explicitly removed it. Otherwise, we omit the key
                // so the backend leaves the existing banner untouched.
                let payload = { ...item };

                // Strip helper / reactive-only fields
                delete payload._banner_uploading;
                delete payload._banner_dirty;

                let data = payload;
                let that = this;

                let s = function(response) {
                    if (response.data.error) {
                        that.$bvToast.toast(
                            response.data.error,
                            { title: "Error", autoHideDelay: 1500, variant: "warning" }
                        );
                    } else {
                        that.$bvToast.toast(
                            "Finance company saved successfully.",
                            { title: "Success", autoHideDelay: 1500, variant: "success" }
                        );
                        item.finance_company_id = response.data.result[0];
                        that.update_packet();

                        // Refetch so the new Azure URL replaces the local base64 preview
                        that.fetch_init();
                    }
                    that.items_being_loaded = false;
                }
                let f = function(error) {
                    console.log(error);
                    that.$bvToast.toast(
                        "Error saving finance company. Please try again.",
                        { title: "Error", autoHideDelay: 1500, variant: "warning" }
                    );
                    that.items_being_loaded = false;
                }
                this.items_being_loaded = true;
                Requestor.postSyncP(path, data, this, s, f);
            },

            update_packet: function() {
                console.log("Update packet if needed");
            },

            fetch_init: function(e) {
                if (e) {
                    e.preventDefault();
                }
                let path = "/api/finance_company/get_all_finance_companies/"
                let data = {}
                let that = this;
                let s = function(response) {
                    if (response.data.error) {
                        that.$bvToast.toast(
                            response.data.error,
                            { title: "Error", autoHideDelay: 1500, variant: "warning" }
                        );
                    } else {
                        let allItems = response.data.result;
                        that.items = allItems.filter(item =>
                            item.company_type === 'finance' ||
                            item.finance_company_type === 'finance'
                        );
                        that.items.forEach(item => {
                            if (!item.finance_company_charges_tax_in_states) {
                                item.finance_company_charges_tax_in_states = [];
                            }
                            if (item.is_active === undefined || item.is_active === null) {
                                item.is_active = true;
                            }
                            // Ensure banner_image exists as a property so v-model works smoothly
                            if (item.banner_image === undefined) {
                                item.banner_image = null;
                            }
                            item._banner_uploading = false;
                            item._banner_dirty = false;
                        });
                    }
                    that.items_being_loaded = false;
                }
                let f = function(error) {
                    console.log(error);
                    that.$bvToast.toast(
                        "Error loading finance companies list. Please refresh.",
                        { title: "Error", autoHideDelay: 1500, variant: "warning" }
                    );
                    that.items_being_loaded = false;
                }
                this.items_being_loaded = true;
                Requestor.getSyncP(path, data, this, s, f);
            },

            add_new: function() {
                let item = {
                    finance_company_id: null,
                    finance_company_name: null,
                    finance_company_phone_number: null,
                    finance_company_website: null,
                    finance_company_address: null,
                    finance_company_charges_tax_in_states: [],
                    is_active: true,
                    banner_image: null,
                    _banner_uploading: false,
                    _banner_dirty: false,
                    ts: null,
                    edit: false,
                }
                this.items.unshift(item);
            },

            // ---------------- Banner image methods ----------------
            handleBannerFileChange(event, item) {
                const file = event.target.files && event.target.files[0];
                if (!file) return;

                if (!file.type.startsWith('image/')) {
                    this.$bvToast.toast("Please select a valid image file.", {
                        title: "Invalid File", autoHideDelay: 2000, variant: "warning"
                    });
                    event.target.value = '';
                    return;
                }

                const MAX_SIZE = 5 * 1024 * 1024;
                if (file.size > MAX_SIZE) {
                    this.$bvToast.toast("Image must be under 5MB.", {
                        title: "File Too Large", autoHideDelay: 2000, variant: "warning"
                    });
                    event.target.value = '';
                    return;
                }

                this.$set(item, '_banner_uploading', true);

                const reader = new FileReader();
                reader.onload = (e) => {
                    // Store base64 in banner_image — will be uploaded to Azure on Save
                    this.$set(item, 'banner_image', e.target.result);
                    this.$set(item, '_banner_uploading', false);
                    this.$set(item, '_banner_dirty', true);
                };
                reader.onerror = () => {
                    this.$set(item, '_banner_uploading', false);
                    this.$bvToast.toast("Could not read the file.", {
                        title: "Error", autoHideDelay: 2000, variant: "danger"
                    });
                };
                reader.readAsDataURL(file);

                // Reset input so the same file can be selected again later
                event.target.value = '';
            },

            removeBanner(item) {
                this.$bvModal.msgBoxConfirm(
                    "Remove the banner image for this company?",
                    {
                        title: "Confirm Removal",
                        okVariant: "danger",
                        okTitle: "Remove",
                        cancelTitle: "Cancel",
                        centered: true
                    }
                ).then(value => {
                    if (value) {
                        // Empty string signals explicit removal to the backend
                        this.$set(item, 'banner_image', '');
                        this.$set(item, '_banner_dirty', true);
                    }
                });
            },

            handleBannerError(item) {
                console.warn("Banner failed to load for:", item.finance_company_name);
                this.$set(item, 'banner_image', null);
            },

            // ---------------- State selection helpers ----------------
            isStateSelected: function(item, stateValue) {
                return item.finance_company_charges_tax_in_states &&
                       item.finance_company_charges_tax_in_states.includes(stateValue);
            },

            toggleState: function(item, stateValue) {
                if (!item.finance_company_charges_tax_in_states) {
                    item.finance_company_charges_tax_in_states = [];
                }

                const index = item.finance_company_charges_tax_in_states.indexOf(stateValue);
                if (index > -1) {
                    item.finance_company_charges_tax_in_states.splice(index, 1);
                } else {
                    item.finance_company_charges_tax_in_states.push(stateValue);
                }
            },

            toggleAllStates: function(item) {
                if (!item.finance_company_charges_tax_in_states) {
                    item.finance_company_charges_tax_in_states = [];
                }

                if (this.isAllStatesSelected(item)) {
                    item.finance_company_charges_tax_in_states = [];
                } else {
                    item.finance_company_charges_tax_in_states = this.states.map(state => state.value);
                }
            },

            isAllStatesSelected: function(item) {
                return item.finance_company_charges_tax_in_states &&
                       item.finance_company_charges_tax_in_states.length === this.states.length;
            },

            getSelectedStatesPreview: function(item) {
                if (!item.finance_company_charges_tax_in_states || item.finance_company_charges_tax_in_states.length === 0) {
                    return 'None';
                }

                const firstFive = item.finance_company_charges_tax_in_states.slice(0, 5).join(', ');
                if (item.finance_company_charges_tax_in_states.length <= 5) {
                    return firstFive;
                } else {
                    return `${firstFive}... (+${item.finance_company_charges_tax_in_states.length - 5} more)`;
                }
            }
        },
        created: function() {
            this.packet = JSON.parse(localStorage.packet);
            this.packet.userDetails = JSON.parse(localStorage.userDetails);
            this.fetch_init();
        }
    }
</script>

<style scoped>
/* ==================== Banner Section ==================== */
.banner-section {
    margin-bottom: 20px;
    padding-bottom: 20px;
    border-bottom: 1px solid #e2e8f0;
}

.banner-upload-row {
    display: flex;
    gap: 20px;
    align-items: flex-start;
    flex-wrap: wrap;
    margin-top: 6px;
}

.banner-preview {
    position: relative;
    width: 320px;
    height: 110px;
    border-radius: 8px;
    overflow: hidden;
    background: #f8fafc;
    border: 1px dashed #cbd5e0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.banner-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.banner-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    color: #a0aec0;
    font-size: 12px;
}

.banner-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
}

.banner-controls {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 220px;
}

.banner-file-input {
    display: none;
}

.banner-upload-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 8px 14px;
    background: #667eea;
    color: #fff;
    border-radius: 8px;
    font-weight: 600;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    margin: 0;
    text-align: center;
}

.banner-upload-btn:hover {
    background: #5a67d8;
    transform: translateY(-1px);
    color: #fff;
}

.banner-remove-btn {
    font-size: 12px;
}

.banner-hint {
    color: #a0aec0;
    font-size: 11px;
}

@media (max-width: 768px) {
    .banner-preview {
        width: 100%;
    }
    .banner-controls {
        width: 100%;
    }
}

/* ==================== Existing styles ==================== */
.company-status {
    flex: 0.5;
    min-width: 150px;
}

.status-toggle {
    display: flex;
    align-items: center;
    gap: 10px;
}

.status-switch {
    margin: 0;
}

.status-switch >>> .custom-control-label {
    cursor: pointer;
    font-weight: 600;
    font-size: 13px;
}

.status-text {
    font-weight: 600;
    font-size: 12px;
    margin-left: 8px;
}

.text-success {
    color: #48bb78;
}

.text-danger {
    color: #f56565;
}

.company-header {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    margin-bottom: 15px;
    padding-bottom: 15px;
    border-bottom: 1px solid #e2e8f0;
    align-items: flex-end;
}

@media (max-width: 768px) {
    .company-status {
        width: 100%;
        min-width: unset;
    }
}

.glass-container {
    min-height: 100vh;
    padding: 20px;
    background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
    background-size: cover;
    background-position: center;
    background-attachment: fixed;
    position: relative;
    overflow: hidden;
}

.glass-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(2px);
    z-index: 0;
}

.finance-companies-container {
    padding: 20px;
    background: transparent;
    min-height: 100vh;
    border-radius: 34px;
    z-index: 1;
    position: relative;
}

.modern-card {
    background: rgba(255, 255, 255, 0.7);
    border-radius: 15px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    padding: 25px;
    margin: 0 auto;
    margin-top: 10px;
    max-width: 1800px;
}

.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 25px;
    padding-bottom: 20px;
    border-bottom: 2px solid #f0f0f0;
}

.title-section .page-title {
    font-size: 1.8rem;
    font-weight: 700;
    color: #2d3748;
    margin: 0;
}

.title-section .page-subtitle {
    color: #718096;
    font-size: 0.95rem;
    margin: 5px 0 0;
}

.action-buttons {
    display: flex;
    gap: 10px;
}

.action-btn {
    padding: 8px 16px;
    font-weight: 600;
    border-radius: 8px;
    transition: all 0.2s ease;
}

.add-btn {
    background: #667eea;
    border: none;
}

.add-btn:hover {
    background: #5a67d8;
    transform: translateY(-1px);
}

.refresh-btn:hover {
    transform: translateY(-1px);
}

.info-alert {
    background: rgba(13, 110, 253, 0.1);
    border: 1px solid rgba(13, 110, 253, 0.2);
    border-radius: 8px;
    margin-bottom: 25px;
}

.alert-content {
    display: flex;
    align-items: flex-start;
}

.alert-content .bi {
    color: #0d6efd;
    margin-top: 2px;
}

.loading-container {
    text-align: center;
    padding: 40px 20px;
}

.company-card {
    background: #ffffff;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    padding: 20px;
    margin-bottom: 15px;
    border: 1px solid #e2e8f0;
    transition: all 0.3s ease;
}

.company-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #cbd5e0;
}

.company-name {
    flex: 2;
    min-width: 300px;
}

.company-phone {
    flex: 1;
    min-width: 200px;
}

.field-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: #718096;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
}

.name-input,
.phone-input,
.website-input,
.address-input {
    background: #f8f9fa;
    border-color: #e2e8f0;
    font-size: 12px;
}

.name-input.new-company {
    background: #fff3cd;
    border-color: #ffeaa7;
    font-weight: bold;
}

.name-input:focus,
.phone-input:focus,
.website-input:focus,
.address-input:focus {
    background: #ffffff;
    border-color: #667eea;
    box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

.company-details-row {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    margin-bottom: 20px;
}

.detail-field {
    flex: 1;
    min-width: 200px;
}

.detail-field.address-field {
    flex: 2;
    min-width: 300px;
}

.tax-section {
    background: #f8fafc;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
    border: 1px solid #e2e8f0;
}

.tax-header {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
}

.tax-header .bi {
    color: #667eea;
}

.tax-title {
    font-size: 16px;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
}

.tax-description {
    color: #718096;
    font-size: 13px;
    margin-bottom: 15px;
}

.states-container {
    background: white;
    border-radius: 6px;
    padding: 15px;
    border: 1px solid #e2e8f0;
}

.states-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 1px solid #e2e8f0;
}

.states-count {
    font-size: 13px;
    font-weight: 600;
    color: #4a5568;
}

.toggle-all-btn {
    font-size: 12px;
    padding: 4px 8px;
}

.states-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 10px;
    max-height: 300px;
    overflow-y: auto;
    padding: 10px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    background: #f9fafb;
}

.state-checkbox-item {
    padding: 8px 10px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.state-checkbox-item:hover {
    border-color: #cbd5e0;
    background: #f7fafc;
}

.state-checkbox-item.selected {
    background: #ebf4ff;
    border-color: #4299e1;
}

.state-checkbox {
    margin: 0;
    width: 100%;
}

.state-checkbox >>> .custom-control-label {
    cursor: pointer;
    width: 100%;
    font-size: 12px;
    color: #4a5568;
}

.state-checkbox-item.selected >>> .custom-control-label {
    color: #2c5282;
    font-weight: 500;
}

.state-label {
    display: inline-block;
    width: 100%;
}

.selected-states-preview {
    margin-top: 15px;
    padding: 10px;
    background: #f0fff4;
    border-radius: 4px;
    border: 1px solid #c6f6d5;
}

.preview-label {
    font-weight: 600;
    color: #276749;
    font-size: 12px;
    margin-right: 8px;
}

.selected-states {
    color: #276749;
    font-size: 12px;
}

.company-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 15px;
    border-top: 1px solid #e2e8f0;
}

.company-info {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.company-id {
    font-size: 12px;
    color: #718096;
    font-weight: 500;
    background: #f8f9fa;
    padding: 3px 8px;
    border-radius: 4px;
    display: inline-block;
}

.action-buttons-wrapper {
    display: flex;
    gap: 10px;
}

.save-btn {
    background: #48bb78;
    border-color: #48bb78;
    color: white;
    padding: 6px 12px;
    font-size: 12px;
}

.save-btn:hover {
    background: #38a169;
    border-color: #38a169;
}

.save-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.cancel-btn {
    color: #f56565;
    border-color: #f56565;
    padding: 6px 12px;
    font-size: 12px;
}

.cancel-btn:hover {
    background: #f56565;
    color: white;
}

.table-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 0;
    margin-top: 15px;
    border-top: 1px solid #e2e8f0;
}

.pagination-info {
    color: #718096;
    font-size: 0.85rem;
}

.empty-state {
    text-align: center;
    padding: 40px 20px;
    border-radius: 8px;
    background: #f8fafc;
    border: 1px dashed #e2e8f0;
}

.empty-state-icon {
    color: #a0aec0;
    margin-bottom: 15px;
}

.empty-state h3 {
    color: #2d3748;
    margin-bottom: 8px;
    font-weight: 600;
}

.empty-state p {
    color: #718096;
    margin-bottom: 15px;
}

.empty-state-actions {
    display: flex;
    gap: 10px;
    justify-content: center;
    flex-wrap: wrap;
}

@media (max-width: 1200px) {
    .company-header {
        gap: 15px;
    }

    .company-name,
    .company-phone {
        min-width: calc(50% - 15px);
    }

    .states-grid {
        grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    }
}

@media (max-width: 768px) {
    .modern-card {
        padding: 15px;
    }

    .header-section {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
    }

    .action-buttons {
        width: 100%;
    }

    .action-btn {
        flex: 1;
        justify-content: center;
    }

    .company-header {
        flex-direction: column;
        gap: 15px;
    }

    .company-name,
    .company-phone {
        width: 100%;
        min-width: unset;
    }

    .company-details-row {
        flex-direction: column;
        gap: 15px;
    }

    .detail-field,
    .detail-field.address-field {
        width: 100%;
        min-width: unset;
    }

    .states-grid {
        grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
    }

    .company-actions {
        flex-direction: column;
        gap: 15px;
        align-items: stretch;
    }

    .action-buttons-wrapper {
        justify-content: flex-end;
    }
}

@media (max-width: 480px) {
    .action-buttons {
        flex-direction: column;
    }

    .action-btn {
        width: 100%;
        justify-content: center;
    }

    .states-header {
        flex-direction: column;
        gap: 10px;
        align-items: flex-start;
    }

    .company-actions {
        align-items: stretch;
    }

    .action-buttons-wrapper {
        flex-direction: column;
    }

    .save-btn,
    .cancel-btn {
        width: 100%;
    }
}
</style>


// WEBPACK FOOTER //
// src/components/ControlPanel/ListFinanceCompanies.vue